import React, { useEffect, useCallback } from 'react';
import { Book, Tag, TAG_CATEGORY_DISPLAY_NAMES } from '../types';
import { ImageGallery } from './ImageGallery';
import './BookDetailModal.css';

export interface BookDetailModalProps {
  /** Livro a ser exibido no modal */
  book: Book | null;
  /** Indica se o modal está aberto */
  isOpen: boolean;
  /** Callback chamado quando o modal é fechado */
  onClose: () => void;
}

/**
 * Componente BookDetailModal
 * 
 * Exibe um modal com detalhes completos do livro incluindo:
 * - Título, descrição e ano de publicação
 * - Galeria de imagens com navegação
 * - Tags organizadas por categoria
 * - Status de disponibilidade física
 * - Botão para fechar e suporte a tecla Escape
 * 
 * Valida: Requisitos 4.3
 */
export function BookDetailModal({
  book,
  isOpen,
  onClose,
}: BookDetailModalProps): React.ReactElement | null {
  // Handle keyboard navigation for Escape key
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen || !book) return;

      if (event.key === 'Escape') {
        onClose();
      }
    },
    [isOpen, book, onClose]
  );

  // Add keyboard event listener
  useEffect(() => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, handleKeyDown]);

  // Don't render if not open or no book
  if (!isOpen || !book) return null;

  const images = book.images || [];

  // Group tags by category
  const tagsByCategory = groupTagsByCategory(book.tags || []);

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="book-detail-modal__overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="book-detail-title"
    >
      <div className="book-detail-modal">
        {/* Mobile Header com título e botão fechar */}
        <div className="book-detail-modal__mobile-header">
          <h2 className="book-detail-modal__mobile-title">{book.title}</h2>
          <button
            type="button"
            className="book-detail-modal__close-button book-detail-modal__close-button--mobile"
            onClick={onClose}
            aria-label="Fechar modal"
          >
            ✕
          </button>
        </div>

        {/* Close Button (desktop) */}
        <button
          type="button"
          className="book-detail-modal__close-button book-detail-modal__close-button--desktop"
          onClick={onClose}
          aria-label="Fechar modal"
        >
          ✕
        </button>

        <div className="book-detail-modal__content">
          {/* Image Gallery Section */}
          <div className="book-detail-modal__gallery">
            <ImageGallery
              images={images}
              boxTitle={book.title}
              enableZoom={true}
              showDots={true}
              showThumbnails={true}
              showArrows={true}
            />
          </div>

          {/* Book Details Section */}
          <div className="book-detail-modal__details">
            {/* Title */}
            <h2 id="book-detail-title" className="book-detail-modal__title">
              {book.title}
            </h2>

            {/* Publication Year */}
            {book.publicationYear && (
              <span className="book-detail-modal__year">
                Ano de publicação: {book.publicationYear}
              </span>
            )}

            {/* Availability Badge */}
            <div className="book-detail-modal__badges">
              <div
                className={`book-detail-modal__availability ${
                  book.physicallyAvailable
                    ? 'book-detail-modal__availability--available'
                    : 'book-detail-modal__availability--unavailable'
                }`}
              >
                <span className="book-detail-modal__availability-icon">
                  {book.physicallyAvailable ? '✓' : '✗'}
                </span>
                <span className="book-detail-modal__availability-text">
                  {book.physicallyAvailable
                    ? 'Disponível fisicamente'
                    : 'Não disponível fisicamente'}
                </span>
              </div>

              {book.isAwarded && (
                <div className="book-detail-modal__awarded">
                  <span className="book-detail-modal__awarded-icon">🏆</span>
                  <span className="book-detail-modal__awarded-text">Livro Premiado</span>
                </div>
              )}
            </div>

            {/* Description */}
            <div className="book-detail-modal__description-section">
              <h3 className="book-detail-modal__section-title">Descrição</h3>
              <p className="book-detail-modal__description">{book.description}</p>
            </div>

            {/* Tags by Category */}
            {tagsByCategory.length > 0 && (
              <div className="book-detail-modal__tags-section">
                <h3 className="book-detail-modal__section-title">Categorias</h3>
                <div className="book-detail-modal__tags-container">
                  {tagsByCategory.map((categoryGroup) => (
                    <div
                      key={categoryGroup.categoryName}
                      className="book-detail-modal__tag-category"
                    >
                      <span className="book-detail-modal__tag-category-name">
                        {categoryGroup.displayName}:
                      </span>
                      <div className="book-detail-modal__tag-list">
                        {categoryGroup.tags.map((tag) => (
                          <span key={tag.id} className="book-detail-modal__tag">
                            {tag.name}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Groups tags by their category
 */
interface TagCategoryGroup {
  categoryName: string;
  displayName: string;
  tags: Tag[];
}

function groupTagsByCategory(tags: Tag[]): TagCategoryGroup[] {
  const categoryMap = new Map<string, TagCategoryGroup>();

  const categoryOrder = [
    'process',
    'paper',
    'finishing',
    'technology',
    'enhancement',
    'work_type',
  ];

  tags.forEach((tag) => {
    const categoryName = tag.category?.name || 'other';
    const displayName =
      tag.category?.displayName ||
      TAG_CATEGORY_DISPLAY_NAMES[categoryName] ||
      'Outros';

    if (!categoryMap.has(categoryName)) {
      categoryMap.set(categoryName, {
        categoryName,
        displayName,
        tags: [],
      });
    }

    categoryMap.get(categoryName)!.tags.push(tag);
  });

  const result: TagCategoryGroup[] = [];
  categoryOrder.forEach((categoryName) => {
    const group = categoryMap.get(categoryName);
    if (group) {
      result.push(group);
      categoryMap.delete(categoryName);
    }
  });

  categoryMap.forEach((group) => {
    result.push(group);
  });

  return result;
}

export default BookDetailModal;
